import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, Clock, Award, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { Button, Avatar, Tag, Loading, EmptyState } from "@/components/brand";
import { CatIcon } from "@/components/icons";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";

export default function ChallengeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const c = useQuery({ queryKey: ["challenge", id], queryFn: () => api(`/challenges/${id}`) });

  const join = useMutation({ mutationFn: () => api(`/challenges/${id}/join`, { method: "POST" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["challenge", id] }); qc.invalidateQueries({ queryKey: ["challenges"] }); toast.success("You joined the challenge!"); }, onError: (e) => toast.error(e.message) });
  const leave = useMutation({ mutationFn: () => api(`/challenges/${id}/leave`, { method: "POST" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["challenge", id] }); qc.invalidateQueries({ queryKey: ["challenges"] }); toast.message("You left the challenge."); }, onError: (e) => toast.error(e.message) });

  if (c.isLoading) return <Loading />;
  if (!c.data) return <><PageHeader /><EmptyState title="Challenge not found" /></>;
  const d = c.data;
  const pct = Math.min(100, Math.round((d.progress / d.goal) * 100));

  return (
    <div data-testid="challenge-detail-screen" className="max-w-2xl">
      <PageHeader onBack={() => navigate(-1)} />
      <div className="p-6 rounded-xl2 bg-brand-soft">
        <div className="flex items-center justify-between">
          <div className="w-14 h-14 rounded-full bg-brand flex items-center justify-center"><CatIcon name={d.badge_icon} className="text-white" size={26} /></div>
          <Tag label={d.state} tone={d.state === "active" ? "pink" : d.state === "upcoming" ? "grey" : "dark"} />
        </div>
        <h1 className="font-display text-3xl text-ink tracking-tight mt-3">{d.title}</h1>
        {d.is_trainer_challenge ? <div className="font-body text-sm text-brand mt-1">Trainer challenge · {d.trainer_name}</div> : null}
        <p className="font-body text-mutedgrey mt-2">{d.description}</p>
        <div className="flex gap-4 mt-4 font-body text-sm text-ink">
          <span className="inline-flex items-center gap-1"><Users size={16} className="text-brand" />{d.participant_count}</span>
          <span className="inline-flex items-center gap-1"><Clock size={16} className="text-brand" />{d.days_left}d left</span>
          <span className="inline-flex items-center gap-1"><Award size={16} className="text-brand" />{d.reward_points} pts</span>
          <span className="inline-flex items-center gap-1"><Trophy size={16} className="text-brand" />{d.goal} {d.metric}</span>
        </div>

        {d.joined ? (
          <div className="mt-4">
            <div className="h-2.5 rounded-pill bg-white overflow-hidden"><div className="h-full bg-brand" style={{ width: `${pct}%` }} /></div>
            <div className="font-body text-xs text-mutedgrey mt-1">{d.progress}/{d.goal} {d.metric} {d.completed ? "· 🏆 completed!" : ""}</div>
          </div>
        ) : null}

        <div className="mt-5">
          {d.joined
            ? (!d.completed ? <Button testID="challenge-leave-button" title="Leave Challenge" variant="outline" loading={leave.isPending} onClick={() => leave.mutate()} /> : null)
            : <Button testID="challenge-join-button" title="Join Challenge" loading={join.isPending} disabled={d.state === "ended"} onClick={() => join.mutate()} />}
        </div>
      </div>

      <h3 className="font-display text-xl text-ink mt-6 mb-3">Leaderboard</h3>
      {(d.leaderboard || []).length === 0 ? (
        <EmptyState icon={Trophy} title="No participants yet" subtitle="Be the first to join and lead the board." />
      ) : (
        <div className="space-y-2">
          {d.leaderboard.map((r) => (
            <div key={r.user_id} className={cn("flex items-center gap-3 p-3 rounded-md border", r.is_me ? "border-brand bg-brand-soft2" : "border-divider")} data-testid={`leaderboard-row-${r.rank}`}>
              <span className="w-7 text-center font-display text-lg text-brand">{r.rank}</span>
              <Avatar uri={r.avatar} size={36} name={r.name} />
              <span className="font-body font-semibold text-sm text-ink flex-1 truncate">{r.name}{r.is_me ? " (You)" : ""}</span>
              {r.completed ? <Trophy size={16} className="text-brand" /> : null}
              <span className="font-display text-brand">{r.progress}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
